<template>
  <div>
    <div class="level is-marginless">
      <div class="level-left">
        <div class="level-item">
          <div class="control has-icons-left">
            <input class="input" :placeholder="$t('search', 'Search')" type="text" v-model="searchFilter">
            <span class="icon is-small is-left">
              <font-awesome-icon icon="search" fixed-width />
            </span>
          </div>
        </div>
        <div class="level-item">
          <p>{{$t('displaying-x-rows-of-total', { replace: { x: sortedData.length, total: data.length }, defaultValue: `Displaying ${sortedData.length} of ${data.length} rows` })}}</p>
        </div>
      </div>
      <div class="level-right">
        <pagination :numItemsTotal="sortedData.length" :current-page.sync="currentPage" :items-per-page.sync="itemsPerPage"  />
      </div>
    </div>
    <div style="overflow-x: auto;">
      <table>
        <thead>
          <th v-for="column in columns" :key="column.key" @click="sortColumn(column)" :class="[column.sortable && 'is-clickable']">
            <div style="display: flex; justify-content: space-between;">
              <span>{{ column.displayName || column.key }}</span>
              <span v-if="column.sortable">
                <font-awesome-icon v-if="column.key === sortedColumnKey" :icon="sortDirection === 'asc' ? 'sort-up' : 'sort-down'" fixed-width />
                <font-awesome-icon v-else icon="sort" fixed-width />
              </span>
            </div>
          </th>
          <th>{{$t('actions', 'Actions')}}</th>
        </thead>
        <tbody>
          <tr v-for="row in pagedData" :key="row.id">
            <td
              v-for="column in columns"
              :key="row.id + '/' + column.key + '/' + row[column.key]"
              :style="{ 'text-align': column.textAlign }"
            >
              <text-highlight v-if="column.searchable !== undefined ? column.searchable : true" :text="formatValue(column.type, row[column.key])" :query="searchFilter" />
              <span v-else>{{formatValue(column.type, row[column.key])}}</span>
            </td>
            <td class="is-flex">
              <a href="" @click.prevent="editRow(row)" :title="$t('edit', 'Edit')">
                <font-awesome-icon icon="pen" fixed-width />
              </a>
              <a href="" @click.prevent="deleteRow(row)" :title="$t('delete', 'Delete')">
                <font-awesome-icon icon="trash" fixed-width />
              </a>
              <slot name="actions" :row="row" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import Pagination from './Pagination'
import TextHighlight from './TextHighlight'

export default {
  name: 'DataTable',
  components: { Pagination, TextHighlight },
  data () {
    return {
      currentPage: 1,
      itemsPerPage: 0,
      sortedColumnKey: null,
      sortDirection: 'asc',
      searchFilter: ''
    }
  },
  props: {
    data: { type: Array, default: () => [] },
    columns: { type: Array, default: () => [] },
    actions: { type: Array, default: () => [] }
  },
  computed: {
    searchableColumns () {
      return this.columns.filter(col => col.searchable !== undefined ? col.searchable : true)
    },
    filteredData () {
      let data = this.data.slice()

      // Apply free text search filter
      if (this.searchFilter) {
        const keywords = this.searchFilter.trim().split(' ').map(keyword => keyword.toLowerCase())

        data = data
          .filter(
            (row) => {
              const values = this.searchableColumns.map(({ key, type }) => this.formatValue(type, row[key]).toLowerCase())

              // Each keyword needs to be present in the values of each row
              return keywords.every(keyword => (values.some(val => val.indexOf(keyword) !== -1)))
            }
          )
      }

      return data
    },
    sortedData () {
      let data = this.filteredData.slice()

      // Sort rows
      if (this.sortedColumnKey) {
        const sortKey = this.sortedColumnKey
        const sortAsc = this.sortDirection === 'asc'

        data = data
          .sort(
            (a, b) => (a[sortKey] < b[sortKey] ? -1 : a[sortKey] > b[sortKey] ? 1 : 0) * (sortAsc ? 1 : -1)
          )
      }

      return data
    },
    pagedData () {
      if (this.itemsPerPage > 0) {
        const idxOffset = (this.currentPage - 1) * this.itemsPerPage
        return this.sortedData.slice(idxOffset, idxOffset + this.itemsPerPage)
      } else {
        return this.sortedData.slice()
      }
    }
  },
  methods: {
    formatValue (type, value) {
      switch (type) {
        case 'datetime': return new Date(value).toLocaleString()
        case 'date': return new Date(value).toLocaleString('default', { year: 'numeric', month: '2-digit', day: '2-digit' })
        default: return value.toString()
      }
    },
    deleteRow (row) {
      if (window.confirm(this.$t('confirm-table-row-deletion', 'Are you sure you want to delete this row?'))) {
        this.$emit('onDeleteRow', row)
      }
    },
    editRow (row) {
      this.$emit('onEditRow', row)
    },
    sortColumn (column) {
      if (!column.sortable) {
        return
      }

      if (this.sortedColumnKey === column.key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortedColumnKey = column.key
        this.sortDirection = 'asc'
      }
    }
  }
}
</script>

<style scoped>
table {
  font-size: 12px;
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
}

thead {
  border-bottom: 1px solid #bcbcbc;
  border-top: 1px solid #bcbcbc;
}

th {
  user-select: none;
}

th, ::v-deep td {
  padding: 4px;
}

tr:nth-child(even) {
  background: #f5f5f5;
}

tr:hover {
  background: #dfdfdf;
}
</style>
